import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Preview } from '../api/generated/types.gen';
import { NOT_CONNECTED, type Action, type Environment, type Order, type OrderIntent } from '../api/trading';
import { money, num, tenths, toTenths } from '../format';

export type Review = {
  kind: 'new' | 'amend'; headline: string; market: string; preview: Preview | null; action: Action; side: 'yes' | 'no'; sideLabel?: string;
  warnings: string[]; blocked?: string | null; environment: Environment | null; connected: boolean; intent: OrderIntent; order?: Order;
  /** Plan 012: the exits this buy goes in with, already worded ("TP 50¢ · SL 25¢, sells no lower than 20¢"). */
  exits?: string | null;
  /** Plan 072: a sell that gets out, into whatever bids exist. */
  closing?: boolean;
};
const TIF_TEXT = { until_close: 'rests until close', immediate_or_cancel: 'immediate or cancel', fill_or_kill: 'all or nothing' } as const;
const HOLD_MS = 700;

/** Nothing is sent without this step. The final button names the exact order and environment. */
export function ConfirmOrder({ review, onClose, onConfirm }: { review: Review; onClose: () => void; onConfirm: (r: Review) => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const edit = useRef<HTMLButtonElement>(null);
  const phone = typeof matchMedia === 'function' && matchMedia('(max-width: 899px)').matches;
  useEffect(() => { edit.current?.focus(); const esc = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); }; window.addEventListener('keydown', esc); return () => window.removeEventListener('keydown', esc); }, [busy, onClose]);
  const p = review.preview, sell = review.action === 'sell';
  // An order resting until close is shown whole: what fills now plus the remainder at its limit.
  const rests = review.intent.time_in_force === 'until_close' && p != null && Number(p.unfilled_quantity) > 0;
  const atLimit = rests ? `${num(p!.unfilled_quantity)} @ ${tenths(toTenths(review.intent.limit_price))} · ${money(p!.unfilled_cost_at_limit ?? '0')}, no fee as maker` : '';
  const go = async () => {
    if (busy || !review.connected || review.blocked) return;
    setBusy(true); setError('');
    // The client order ID was fixed when this review opened, so a retry cannot place a second order.
    try { await onConfirm(review); } catch (e) { setError(e instanceof Error ? e.message : 'The order was not accepted.'); } finally { setBusy(false); }
  };
  const env = review.environment;
  const label = `${review.kind === 'amend' ? 'Confirm change' : sell ? 'Sell' : 'Buy'} ${review.intent.quantity} ${review.sideLabel ?? review.side.toUpperCase()}`;
  return <>
    <div className="scrim modal-scrim" onClick={() => !busy && onClose()}/>
    <div className="confirm" role="dialog" aria-modal="true" aria-labelledby="confirm-title" data-testid="confirm">
      <div className="confirm-body">
        <h3 id="confirm-title">{review.kind === 'amend' ? 'Confirm change' : sell ? 'Confirm sell' : 'Confirm buy'} · <span className={`env ${env ?? 'none'}`}>{env ? env.toUpperCase() : 'NOT CONNECTED'}</span></h3>
        <p className="faint">{review.market}</p>
        <dl>
          <dt>Order</dt><dd>{review.headline} · {TIF_TEXT[review.intent.time_in_force]}</dd>
          {review.exits !== undefined && <><dt>Exits</dt><dd data-testid="confirm-exits">{review.exits ?? 'None: no take-profit or stop-loss'}</dd></>}
          {sell ? rests && p?.received_if_all_fill != null ? <>
            <dt>Proceeds now</dt><dd>{money(p.proceeds)}</dd>
            <dt>Exit fee (est.)</dt><dd>−{money(p.fee_estimate ?? '0')}</dd>
            <dt>Rests until close</dt><dd>{atLimit}</dd>
            <dt>Receive if all fill</dt><dd className="total">{money(p.received_if_all_fill)}</dd>
            <dt>Realised P&amp;L (all fill)</dt><dd className={pnlClass(p.realized_if_all_fill)}>{pnl(p.realized_if_all_fill)}</dd>
          </> : <>
            <dt>Proceeds</dt><dd>{money(p?.proceeds)}</dd>
            <dt>Exit fee (est.)</dt><dd>{p?.fee_estimate ? `−${money(p.fee_estimate)}` : '—'}</dd>
            <dt>You receive</dt><dd className="total">{money(p?.received)}</dd>
            <dt>Realised P&amp;L</dt><dd className={pnlClass(p?.realized_pnl)}>{pnl(p?.realized_pnl)}</dd>
          </> : rests && p?.total_if_all_fill != null ? <>
            <dt>Cost now</dt><dd>{money(p.purchase_cost)}</dd>
            <dt>Fees (est.)</dt><dd>{money(p.fee_estimate ?? '0')}</dd>
            <dt>Rests until close</dt><dd>{atLimit}</dd>
            <dt>Total if all fill</dt><dd className="total">{money(p.total_if_all_fill)}</dd>
            <dt>Maximum loss</dt><dd className="down">−{money(p.total_if_all_fill)}</dd>
          </> : <>
            <dt>Cost</dt><dd>{money(p?.purchase_cost)}</dd>
            <dt>Fees (est.)</dt><dd>{money(p?.fee_estimate)}</dd>
            <dt>Total</dt><dd className="total">{money(p?.total_cost)}</dd>
            <dt>Maximum loss</dt><dd className="down">{p?.maximum_loss ? `−${money(p.maximum_loss)}` : '—'}</dd>
          </>}
        </dl>
        {!p && <p className="note">No current estimate. The order is priced by your limit only.</p>}
        {review.warnings.map(w => <p key={w} className="note">{w}</p>)}
        {review.blocked && <p className="note blocked" data-testid="blocked">{review.blocked}</p>}
        {!review.connected && <p className="note blocked" data-testid="not-connected">{NOT_CONNECTED}</p>}
        {error && <p className="note error" role="alert">{error}</p>}
      </div>
      <div className="confirm-actions">
        <button ref={edit} className="tool" onClick={onClose} disabled={busy}>Edit</button>
        {phone ? <HoldButton className={`go ${review.action}`} disabled={!review.connected || busy || !!review.blocked} onHold={go}>{busy ? 'Sending…' : `Hold to ${label.toLowerCase()}`}</HoldButton>
          : <button className={`go ${review.action}`} data-testid="confirm-send" disabled={!review.connected || busy || !!review.blocked} onClick={go}>{busy ? 'Sending…' : label}</button>}
      </div>
    </div>
  </>;
}

const pnl = (v: string | null | undefined) => v == null ? '—' : `${Number(v) >= 0 ? '+' : '−'}${money(Math.abs(Number(v)))}`;
const pnlClass = (v: string | null | undefined) => v == null ? '' : Number(v) >= 0 ? 'up' : 'down';

/** Press and hold to confirm on touch screens, so a stray tap cannot place an order. */
function HoldButton({ onHold, disabled, className, children }: { onHold: () => void; disabled: boolean; className: string; children: ReactNode }) {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [holding, setHolding] = useState(false);
  const stop = () => { clearTimeout(timer.current); setHolding(false); };
  return <button className={`${className}${holding ? ' holding' : ''}`} disabled={disabled} style={{ ['--hold' as string]: `${HOLD_MS}ms` }}
    onPointerDown={() => { if (disabled) return; setHolding(true); timer.current = setTimeout(() => { setHolding(false); onHold(); }, HOLD_MS); }}
    onPointerUp={stop} onPointerLeave={stop} onPointerCancel={stop}>{children}</button>;
}
